import React from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Button } from "antd";
import { MousePointerClick, Telescope } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import content from "../../../utils/content";

const HeroSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const controls = useAnimation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  // Wähle die Übersetzungen basierend auf der aktuellen Sprache
  const t = content[language].hero;

  if (inView) {
    controls.start("visible");
  }

  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: "easeOut", delay },
    },
  });

  return (
    <div
      className="w-full px-8 md:px-12 py-12 pb-0 md:py-12 overflow-x-hidden overflow-y-hidden my-12"
      id="Home"
    >
      <div
        ref={ref}
        className="grid gap-6 lg:grid-cols-[40%_30%_30%] lg:gap-12 items-start relative"
      >
        {/* Linke Spalte */}
        <motion.div
          variants={fadeInUp(0)}
          initial="hidden"
          animate={controls}
          className="flex flex-col justify-center gap-8 lg:self-end items-center lg:items-start lg:text-left text-center"
        >
          <h1 className="text-4xl md:text-6xl font-bold leading-tight tracking-tighter">
            <span className="text-[#FF7051]">{t.title.highlight}</span>
            {t.title.rest}
          </h1>
          <p className="max-w-[600px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
            {t.subtitle}
          </p>
          <Button
            className="bg-[#FF7051] text-white hover:bg-[#FF7051]/90 w-[50%] h-[3rem] flex items-center justify-center gap-2"
            onClick={() => navigate("/login")}
          >
            <MousePointerClick className="w-4 h-4" />
            {t.button}
          </Button>
        </motion.div>

        {/* Mittlere Spalte */}
        <motion.div
          variants={fadeInUp(0.3)}
          initial="hidden"
          animate={controls}
          className="relative shadow-2xl rounded-2xl p-8 bg-white z-20 lg:mt-16 mt-12 -mb-24 lg:-mb-0 self-end order-1 lg:order-none"
        >
          <div className="inline-block rounded-lg bg-[#FF7051]/10 p-3 mb-4">
            <Telescope className="w-4 h-4 text-[#FF7051]" />
          </div>
          <h3 className="text-md font-bold">{t.middle.label}</h3>
          <p className="text-gray-500 text-xs">{t.middle.description}</p>
        </motion.div>

        {/* Rechte Spalte */}
        <motion.div
          variants={fadeInUp(0.6)}
          initial="hidden"
          animate={controls}
          className="relative w-full lg:h-full h-[400px] flex justify-center items-center z-10 order-2 lg:order-none"
        >
          <img
            src="/HeaderImage.png"
            alt="stars"
            className="h-[100%] lg:h-full lg:-translate-x-[35%]"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default HeroSection;
